// ============================================================
// @yourownmetric/shared — Utilities
// Shared helpers used across all packages
// ============================================================
/**
 * Generate a prefixed unique ID (nanoid-like, no external dependency)
 * Uses crypto.randomUUID() which is available in Workers, Node 19+, and browsers
 */
export function generateId(prefix) {
    const uuid = crypto.randomUUID().replace(/-/g, '');
    return prefix ? `${prefix}_${uuid}` : uuid;
}
/**
 * Generate an API key with prefix.
 * Format: yom_{type}_{env}_{32chars}
 *
 * @example generateAPIKey('pk', 'live') → 'yom_pk_live_a1b2c3d4e5f6...'
 */
export function generateAPIKey(type, env) {
    const random = crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '');
    return `yom_${type}_${env}_${random.slice(0, 32)}`;
}
/**
 * Extract the prefix from an API key (first 15 chars: yom_pk_live_xxx)
 * Used for lookup without exposing the full key
 */
export function extractKeyPrefix(key) {
    return key.slice(0, 15);
}
/**
 * Canonical KV key for tenant -> D1 mapping.
 * Keep this in shared to avoid key-format drift across workers.
 */
export function tenantDbKey(tenantId) {
    return `tenant-db:${tenantId}`;
}
/**
 * SHA-256 hash a string. Works in Workers (crypto.subtle) and Node.js
 */
export async function sha256(input) {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}
/**
 * Daily rotating salt for the cookieless visitor hash (sha256(salt:ip:ua)).
 * With `secret` (ingest's VISITOR_HASH_SECRET): HMAC-SHA256(secret, projectId:day), so nobody
 * without the secret can recompute a day's salt and brute-force IPs from a stored hash.
 * Without it: the legacy SHA-256(projectId:day), which is public and only kept as a fallback.
 */
export async function getDailySalt(projectId, date, secret) {
    const d = date ?? new Date();
    const dayStr = d.toISOString().slice(0, 10); // YYYY-MM-DD
    if (!secret) {
        return sha256(`${projectId}:${dayStr}`);
    }
    const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const mac = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(`${projectId}:${dayStr}`));
    return Array.from(new Uint8Array(mac))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}
/**
 * Validate a tenant slug (URL-safe, lowercase, 3-48 chars)
 */
export function isValidSlug(slug) {
    return /^[a-z0-9][a-z0-9-]{1,46}[a-z0-9]$/.test(slug);
}
/**
 * Validate an email address (basic RFC 5322)
 */
export function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
/**
 * Truncate IP address for anonymization (keep first 3 octets for IPv4, first 3 groups for IPv6)
 */
export function truncateIP(ip) {
    if (ip.includes(':')) {
        // IPv6: keep first 3 groups
        const parts = ip.split(':');
        return parts.slice(0, 3).join(':') + '::';
    }
    // IPv4: zero out last octet
    const parts = ip.split('.');
    parts[3] = '0';
    return parts.join('.');
}
/**
 * Parse a User-Agent string into basic device info (lightweight, no library)
 */
export function parseUserAgent(ua) {
    const lowerUA = ua.toLowerCase();
    // Bot detection
    if (/bot|crawl|spider|slurp|lighthouse|pagespeed|headless/i.test(ua)) {
        return { type: 'bot', browser: 'Bot', os: 'Bot' };
    }
    // Device type
    let type = 'desktop';
    if (/tablet|ipad|playbook|silk/i.test(ua)) {
        type = 'tablet';
    }
    else if (/mobile|android|iphone|ipod|opera mini|iemobile/i.test(ua)) {
        type = 'mobile';
    }
    // Browser
    let browser = 'Unknown';
    if (lowerUA.includes('firefox/')) {
        browser = 'Firefox';
    }
    else if (lowerUA.includes('edg/')) {
        browser = 'Edge';
    }
    else if (lowerUA.includes('opr/') || lowerUA.includes('opera')) {
        browser = 'Opera';
    }
    else if (lowerUA.includes('chrome/') && lowerUA.includes('safari/')) {
        browser = 'Chrome';
    }
    else if (lowerUA.includes('safari/') && !lowerUA.includes('chrome')) {
        browser = 'Safari';
    }
    // OS — Check iPhone/iPad BEFORE macOS since iOS UA strings contain "Mac OS X"
    let os = 'Unknown';
    if (lowerUA.includes('iphone') || lowerUA.includes('ipad') || lowerUA.includes('ipod')) {
        os = 'iOS';
    }
    else if (lowerUA.includes('android')) {
        os = 'Android';
    }
    else if (lowerUA.includes('windows')) {
        os = 'Windows';
    }
    else if (lowerUA.includes('mac os') || lowerUA.includes('macintosh')) {
        os = 'macOS';
    }
    else if (lowerUA.includes('linux')) {
        os = 'Linux';
    }
    return { type, browser, os };
}
/**
 * Format a number with K/M/B suffixes
 */
export function formatNumber(num) {
    if (num >= 1_000_000_000) {
        return `${(num / 1_000_000_000).toFixed(1)}B`;
    }
    if (num >= 1_000_000) {
        return `${(num / 1_000_000).toFixed(1)}M`;
    }
    if (num >= 1_000) {
        return `${(num / 1_000).toFixed(1)}K`;
    }
    return num.toString();
}
/**
 * Get current ISO date string (day granularity)
 */
export function todayISO() {
    return new Date().toISOString().slice(0, 10);
}
/**
 * Get current ISO hour string
 */
export function currentHourISO() {
    return new Date().toISOString().slice(0, 13) + ':00:00';
}
/**
 * Clamp a number between min and max
 */
export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
// ── Password Hashing (bcrypt) ──────────────────────────────────
import { hash as bcryptHash, compare as bcryptCompare } from 'bcryptjs';
/** Cost factor for bcrypt. 10 is the standard balance of speed vs security. */
const BCRYPT_ROUNDS = 10;
/**
 * Hash a password using bcrypt.
 * Returns the full bcrypt hash string (includes salt + algorithm + cost).
 */
export async function hashPassword(password) {
    return bcryptHash(password, BCRYPT_ROUNDS);
}
/**
 * Legacy salted SHA-256 hashes (`salt:hex`, from before bcrypt) are accepted until this
 * date so their owners can sign in once and be rehashed to bcrypt (`passwordNeedsRehash`).
 * After it they no longer verify: those accounts reset their password by email.
 */
export const LEGACY_PASSWORD_HASHES_ACCEPTED_UNTIL = '2027-01-31T00:00:00Z';
/** True for a stored hash that should be replaced with a fresh bcrypt hash after a successful sign-in. */
export function passwordNeedsRehash(storedHash) {
    const m = /^\$2[aby]\$(\d{2})\$/.exec(storedHash);
    return !m || Number(m[1]) < BCRYPT_ROUNDS;
}
/**
 * Verify a plaintext password against a bcrypt hash.
 * Legacy SHA-256 hashes (format: `salt:hex`) verify only until
 * LEGACY_PASSWORD_HASHES_ACCEPTED_UNTIL; callers rehash on success.
 */
export async function verifyPassword(password, storedHash, now = Date.now()) {
    // Legacy SHA-256 format: "uuid-salt:hex-hash" (no $ prefix)
    if (!storedHash.startsWith('$')) {
        if (now >= Date.parse(LEGACY_PASSWORD_HASHES_ACCEPTED_UNTIL)) {
            return false;
        }
        const [salt, hash] = storedHash.split(':');
        if (!salt || !hash) {
            return false;
        }
        const inputHash = await sha256(`${salt}:${password}`);
        return inputHash === hash;
    }
    // bcrypt format: $2a$10$... or $2b$10$...
    return bcryptCompare(password, storedHash);
}
//# sourceMappingURL=index.js.map